import { useEffect, useState, useCallback, useRef } from "react";
import { useI18n } from "@/contexts/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "vta_app_version";
const POLL_MS = 30000;

/**
 * UpdateGate — polls /api/system/version and shows a top banner whenever
 * the server version is greater than what the client has cached.
 *
 * Pressing "تحديث الآن":
 *   1) clears every Cache Storage entry
 *   2) unregisters every Service Worker
 *   3) clears the version key from localStorage
 *   4) hard-reloads with a cache-buster query
 */
export default function UpdateGate({ children }) {
  const { lang, theme } = useI18n();
  const [serverVersion, setServerVersion] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const localVersionRef = useRef(null);

  // Initialize local version once
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      localVersionRef.current = stored ? Number(stored) : null;
    } catch {
      localVersionRef.current = null;
    }
  }, []);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`${API}/system/version`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      const v = Number(data.version || 1);
      setMessage(data.message || null);

      if (localVersionRef.current == null) {
        // First run — adopt the server version silently
        localVersionRef.current = v;
        try { localStorage.setItem(LS_KEY, String(v)); } catch { /* ignore */ }
        return;
      }
      if (v > localVersionRef.current) {
        setServerVersion(v);
      }
    } catch {
      /* offline — silent */
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  const applyUpdate = useCallback(async () => {
    setBusy(true);
    try {
      // 1) Clear every Cache Storage entry (PWA cache, axios cache, …)
      if (typeof caches !== "undefined") {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        } catch { /* ignore */ }
      }
      // 2) Unregister every service worker so the next fetch goes to the network
      if ("serviceWorker" in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        } catch { /* ignore */ }
      }
      // 3) Wipe all session storage + non-auth localStorage so any stale
      //    in-memory state on next boot is rebuilt fresh. We deliberately
      //    KEEP the auth token so the user isn't logged out by an update.
      try { sessionStorage.clear(); } catch { /* ignore */ }
      try {
        const keepKeys = new Set(["vta_token"]);
        const toDrop = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && !keepKeys.has(k)) toDrop.push(k);
        }
        toDrop.forEach((k) => localStorage.removeItem(k));
      } catch { /* ignore */ }
      // 4) Persist the new version so we don't loop the banner
      try {
        localStorage.setItem(LS_KEY, String(serverVersion));
      } catch { /* ignore */ }
      // 5) Hard reload with two cache-busters + replace() to drop history
      //    entry — this prevents the back button from returning to the
      //    stale page.
      const url = new URL(window.location.href);
      url.searchParams.set("v", String(serverVersion));
      url.searchParams.set("_t", String(Date.now()));
      window.location.replace(url.toString());
    } finally {
      setBusy(false);
    }
  }, [serverVersion]);

  /** Manual force-refresh — exposed via the System Health page so STAS can
   *  trigger the same flow on demand even when no banner is showing. */
  const forceRefresh = useCallback(async () => {
    setServerVersion((v) => v ?? Date.now());
    // microtask delay so the state propagates before applyUpdate reads it
    setTimeout(() => applyUpdate(), 0);
  }, [applyUpdate]);

  // Expose globally so any page can call window.__vtaForceRefresh()
  useEffect(() => {
    window.__vtaForceRefresh = forceRefresh;
    return () => { delete window.__vtaForceRefresh; };
  }, [forceRefresh]);

  const show = serverVersion != null;

  const T = lang === "ar"
    ? {
        title: "يوجد تحديث جديد للنظام",
        body: "اضغط للتحديث الآن — سيتم مسح الكاش وتحميل آخر نسخة تلقائياً",
        cta: "تحديث الآن",
        wait: "جاري التحديث…",
      }
    : {
        title: "A new version is available",
        body: "Click to update now — cache will be cleared and the latest version loaded",
        cta: "Update now",
        wait: "Updating…",
      };

  return (
    <>
      {show && (
        <div
          data-testid="update-banner"
          style={{
            position: "sticky", top: 0, zIndex: 10000,
            background: theme?.goldGradient || "linear-gradient(90deg,#caa452,#a4853e)",
            color: theme?.name === "dark" ? "#0a0a0a" : "#fff",
            padding: "10px 16px",
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            justifyContent: "center",
            fontFamily: "'Tajawal','Segoe UI',sans-serif",
            boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
            animation: "vta-pulse 2.6s ease-in-out infinite",
          }}
        >
          <style>{`
            @keyframes vta-pulse {
              0%,100% { filter: brightness(1); }
              50% { filter: brightness(1.07); }
            }
          `}</style>
          <div style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
            <strong style={{ fontWeight: 900, fontSize: 14 }}>
              {T.title}
            </strong>
            <span style={{ fontSize: 12, opacity: 0.9 }}>
              {message ? message : T.body}
            </span>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={applyUpdate}
            data-testid="update-banner-cta"
            style={{
              background: "#fff",
              color: "#7a5e22",
              border: "none",
              borderRadius: 999,
              padding: "8px 18px",
              fontWeight: 900,
              fontSize: 13,
              cursor: busy ? "wait" : "pointer",
              letterSpacing: "0.3px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            }}
          >
            {busy ? T.wait : T.cta}
          </button>
        </div>
      )}
      {children}
    </>
  );
}
